import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Tooltip } from "react-tooltip";
import { format } from "date-fns";
import { useLocation } from "react-router-dom"; // Importar hooks de react-router-dom
import { useShoppingContext } from "../../../../context/ShoppingContext";
import "./AddControlAvesForm.css";

const AddControlAvesForm = () => {
  const [step, setStep] = useState(1);
  const [motivosMuerte, setMotivosMuerte] = useState([]);
  const [currentMotivoIndex, setCurrentMotivoIndex] = useState(0);
  const [otroMotivo, setOtroMotivo] = useState(""); // Para almacenar el motivo personalizado si "otro" es seleccionado
  const [useOtroMotivo, setUseOtroMotivo] = useState(false);
  const location = useLocation(); // Obtener la ruta actual
  const {
    aves,
    setShowAddControlAves,
    fetchControlAves,
    getAveNombre,
    granjaActiva,
    setLoading,
    renderDateTitle,
    selectedAve,
    setSelectedAve,
    fetchMisGalpones,
    fetchMisAves,
    fetchMisGranjas,
  } = useShoppingContext();

  // Avanzar automáticamente al paso 2 si hay un ave seleccionada (selectedAve)
  useEffect(() => {
    if (location.pathname === "/mis-granjas") setSelectedAve(null);
    if (selectedAve) {
      setFormData((prev) => ({ ...prev, ave_id: selectedAve.id }));
      setStep(2); // Saltar la selección de lote si ya hay un ave seleccionada
    }
  }, [setSelectedAve, selectedAve, location.pathname]);

  const getInitialSaldo = useCallback(
    (aveId) => {
      const ave = aves.find((ave) => ave.id === aveId);
      return ave ? ave.saldo : 0;
    },
    [aves]
  );

  const [formData, setFormData] = useState({
    ave_id: "", // Si hay un ave seleccionada, usar su ID
    fecha: format(new Date(), "yyyy-MM-dd"),
    mortalidad: 0,
    saldo: selectedAve ? getInitialSaldo(selectedAve.id) : 0,
    granjaId: granjaActiva,
    death: false, // nuevo campo añadido
  });

  useEffect(() => {
    const saldoInicial = getInitialSaldo(formData.ave_id);
    const nuevoSaldo = saldoInicial - formData.mortalidad;
    const death = formData.mortalidad > 0;

    setFormData((prev) => ({ ...prev, saldo: nuevoSaldo, death }));
  }, [formData.ave_id, formData.mortalidad, getInitialSaldo]);

  const validateInput = (name, value) => {
    let isValid = true;
    const numericValue = parseFloat(value);

    if (!isNaN(numericValue) && numericValue < 0) {
      alert("Por favor, ingresa un número positivo.");
      isValid = false;
    }

    if (numericValue > 10000) {
      alert("El número ingresado es demasiado alto.");
      isValid = false;
    }

    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (validateInput(name, value)) {
      setFormData((prev) => {
        const updatedValue = isNaN(parseFloat(value))
          ? value
          : parseFloat(value);
        return { ...prev, [name]: updatedValue };
      });
    }
  };

  const handleFocus = (e) => {
    if (e.target.value === "0") {
      e.target.value = "";
    }
  };

  const handleDateChange = (e) => {
    const dateValue = e.target.value;
    setFormData((prev) => ({
      ...prev,
      fecha: dateValue, // Ya no convertimos a `Date` para evitar problemas de formato
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    e.target.value = value === "" ? "0" : value;
    if (validateInput(name, e.target.value)) {
      setFormData((prevData) => {
        const updatedValue = parseFloat(e.target.value);
        return { ...prevData, [name]: updatedValue };
      });
    }
  };

  const handleSelectAve = (id) => {
    setFormData((prev) => ({ ...prev, ave_id: id }));
  };

  const handleNextStep = () => {
    if (step === 1 && !formData.ave_id) {
      alert("Por favor, seleccione un Lote antes de continuar.");
      return;
    }

    if (step === 2 && formData.mortalidad > 0) {
      setMotivosMuerte(Array.from({ length: formData.mortalidad }, () => ""));
    }

    setStep(step + 1);
  };

  const handlePreviousStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
    // Al hacer clic en "Regresar" en el paso 2, ocultar el formulario y redirigir a otra página.
    if (step === 2 && location.pathname !== "/mis-granjas") {
      setShowAddControlAves(false);
    }
  };

  // Define the missing functions for motivos management
  const handleMotivoChange = (e) => {
    const value = e.target.value;

    if (value === "otro") {
      setUseOtroMotivo(true);
    } else {
      setUseOtroMotivo(false);
      const updatedMotivos = [...motivosMuerte];
      updatedMotivos[currentMotivoIndex] = value;
      setMotivosMuerte(updatedMotivos);
    }
  };

  const handleOtroMotivoChange = (e) => {
    setOtroMotivo(e.target.value);
  };

  const handleNextMotivo = () => {
    if (useOtroMotivo) {
      const updatedMotivos = [...motivosMuerte];
      updatedMotivos[currentMotivoIndex] = otroMotivo;
      setMotivosMuerte(updatedMotivos);
      setOtroMotivo(""); // Resetea el campo de "otro"
    }

    if (currentMotivoIndex < motivosMuerte.length - 1) {
      setCurrentMotivoIndex(currentMotivoIndex + 1);
    } else {
      setStep(step + 1);
    }
  };

  const handlePreviousMotivo = () => {
    if (currentMotivoIndex > 0) {
      setCurrentMotivoIndex(currentMotivoIndex - 1);
    } else {
      handlePreviousStep();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const records =
      motivosMuerte.length > 0
        ? motivosMuerte.map((motivo) => ({
            ...formData,
            motivo_muerte: motivo,
          }))
        : [{ ...formData, motivo_muerte: null }];

    setShowAddControlAves(false);
    setLoading("Actualizando aves");

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/control-aves`, records);

      window.alert("Registros de control de aves creados exitosamente");
      fetchMisGalpones();
      fetchMisAves();
      fetchMisGranjas();
      fetchControlAves();
      resetFormData();
      setLoading(false);
    } catch (error) {
      console.error("Error al agregar los registros de control de aves:", error);
      alert("Hubo un problema al guardar la información. Intente nuevamente.");
    }
  };

  const resetFormData = () => {
    setFormData({
      ave_id: "",
      fecha: format(new Date(), "yyyy-MM-dd"),
      granjaId: granjaActiva,
    });
    setMotivosMuerte([]);
    setCurrentMotivoIndex(0);
    setUseOtroMotivo(false);
    setOtroMotivo("");
  };

  useEffect(() => {
    // Verifica la ruta actual y resetea el formulario si la ruta es "/mis-granjas"
    if (location.pathname === "/mis-granjas") {
      setStep(1);
      resetFormData();
    }
  }, [location.pathname]);

  return (
    <div className="add-form-container">
      <form className="add-control-aves-form" onSubmit={handleSubmit}>
        <h2 className="form-title">
          {step === 4
            ? "Confirmación"
            : formData.ave_id
            ? `${getAveNombre(formData.ave_id)}`
            : "Seleccionar Ave"}
        </h2>

        {step === 1 && (
          <div>
            <div className="form-group">
              <div className="aves-container">
                {aves.map((ave) => (
                  <div
                    key={ave.id}
                    className={`ave-item ${
                      formData.ave_id === ave.id ? "selected" : ""
                    }`}
                    onClick={() => handleSelectAve(ave.id)}
                    data-tip={`Lote: ${ave.nombre} `}
                  >
                    {ave.nombre} {ave.saldo} aves
                  </div>
                ))}
                <Tooltip place="top" effect="solid" />
              </div>
            </div>

            <div className="add-ave-form-buttons">
              <button
                type="button"
                className={"btn btn-green-outline"}
                onClick={() => setShowAddControlAves(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={"btn btn-green"}
                onClick={handleNextStep}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="form-group">
              <label htmlFor="fecha">Fecha</label>
              <input
                type="date"
                id="fecha"
                name="fecha"
                value={formData.fecha}
                onChange={handleDateChange}
                className="add-ave-form-input"
                max={format(new Date(), "yyyy-MM-dd")}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="mortalidad">¿Cuántas Aves restar?</label>
              <input
                type="number"
                id="mortalidad"
                name="mortalidad"
                value={formData.mortalidad}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                required
                className="aves-input"
                inputMode="numeric"
              />
            </div>

            <div className="add-ave-form-buttons">
              <button
                type="button"
                className={"btn btn-green-outline"}
                onClick={handlePreviousStep}
              >
                Regresar
              </button>
              <button
                type="button"
                className={"btn btn-green"}
                onClick={handleNextStep}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            {formData.mortalidad === 0 ? (
              <>
                <h3>Confirmación</h3>
                <p>
                  Se restarán <strong>{formData.mortalidad}</strong> aves
                  del lote{" "}
                  <strong>{getAveNombre(formData.ave_id)}</strong> con la fecha{" "}
                  <strong>{renderDateTitle(formData.fecha)}</strong>.
                </p>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="motivo_muerte">
                    Motivo de Muerte para Ave {currentMotivoIndex + 1} de{" "}
                    {formData.mortalidad}
                  </label>
                  <select
                    id="motivo_muerte"
                    name="motivo_muerte"
                    value={motivosMuerte[currentMotivoIndex] || ""}
                    onChange={handleMotivoChange}
                    className="aves-select"
                    required
                  >
                    <option value="">Seleccionar motivo</option>
                    <option value="Enfermedad">Enfermedad</option>
                    <option value="Asfixia">Asfixia</option>
                    <option value="Picaje">Picaje</option>
                    <option value="Ataque de predador">
                      Ataque de predador
                    </option>
                    <option value="Accidente">Accidente</option>
                    <option value="Parásitos">Parásitos</option>
                    <option value="Prolapso">Prolapso</option>
                    <option value="otro">Otro</option>
                  </select>

                  {useOtroMotivo && (
                    <input
                      type="text"
                      id="otro_motivo"
                      name="otro_motivo"
                      value={otroMotivo}
                      onChange={handleOtroMotivoChange}
                      placeholder="Ingrese la razón de la mortalidad"
                      className="aves-input"
                      required
                    />
                  )}
                </div>
              </>
            )}

            <div className="add-ave-form-buttons">
              <button
                type="button"
                className={"btn btn-green-outline"}
                onClick={handlePreviousMotivo}
              >
                Regresar
              </button>
              <button
                type="button"
                className={"btn btn-green"}
                onClick={handleNextMotivo}
              >
                {currentMotivoIndex < motivosMuerte.length - 1
                  ? "Siguiente"
                  : "Finalizar"}
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <>
            <p>
              Se registrarán <strong>{formData.mortalidad}</strong> aves muertas
              en el lote <strong>{getAveNombre(formData.ave_id)}</strong> con la
              fecha <strong>{renderDateTitle(formData.fecha)}</strong>.
            </p>

            <div className="add-ave-form-buttons">
              <button
                type="button"
                className={"btn btn-green-outline"}
                onClick={handlePreviousStep}
              >
                Regresar
              </button>
              <button type="submit" className={"btn btn-green"}>
                Confirmar
              </button>
            </div>
          </>
        )}
        <Tooltip />
      </form>
    </div>
  );
};

export default AddControlAvesForm;
